/* Complete the set upsell */
(() => {
  if (customElements.get('complete-the-set')) return;

  const formatMoney = (cents, format) => {
    const fmt = format || '${{amount}}';
    const value = Number(cents) || 0;
    const withDelimiters = (num, precision, thousands, decimal) => {
      const fixed = (num / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return frac ? grouped + decimal + frac : grouped;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'", '.');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  };

  const sameExcept = (a, b, skipIndex) =>
    a.options.every((opt, i) => i === skipIndex || opt === b.options[i]);

  class CompleteTheSet extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        this.data = JSON.parse(this.dataset.ctsJson);
      } catch (e) {
        console.error('[complete-the-set] Could not read product data', e);
        return;
      }

      this.scope =
        this.closest('product-info') ||
        this.closest('.product__info-container') ||
        this.closest('.product') ||
        this.closest('section') ||
        document.body;

      this.toggle = this.querySelector('[data-cts-toggle]');
      this.pills = Array.from(this.querySelectorAll('[data-cts-size]'));
      this.submit = this.querySelector('[data-cts-submit]');
      this.submitLabel = this.querySelector('[data-cts-submit-label]');
      this.errorEl = this.querySelector('[data-cts-error]');
      this.badge = this.querySelector('[data-cts-badge]');
      this.badgeText = this.querySelector('[data-cts-badge-text]') || this.badge;
      this.priceEl = this.querySelector('[data-cts-price]');
      this.compareEl = this.querySelector('[data-cts-compare]');
      this.variantTitleEl = this.querySelector('[data-cts-variant-title]');
      this.imageEl = this.querySelector('[data-cts-image]');

      const up = this.data.upsell;
      this.upsellVariants = up.variants;
      this.sizeIndex = up.sizeIndex;
      this.upsell =
        this.upsellVariants.find((v) => v.id === up.initialId) || this.upsellVariants[0];
      this.userPickedSize = false;
      this.lastMainKey = null;

      this.toggle.addEventListener('change', () => this.applyChecked());
      this.pills.forEach((pill) =>
        pill.addEventListener('click', () => this.onPillClick(pill))
      );
      this.submit.addEventListener('click', () => this.addBoth());

      this.onDocChange = () => this.syncMain();
      document.addEventListener('change', this.onDocChange);
      if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
        this.unsubscribe = window.subscribe(window.PUB_SUB_EVENTS.variantChange, () =>
          setTimeout(() => this.syncMain(), 0)
        );
      }
      // Safety net for themes that swap the variant input without firing events.
      this.poll = setInterval(() => this.syncMain(), 600);

      this.setupSticky();
      this.applyChecked();
      this.syncMain(true);
    }

    disconnectedCallback() {
      clearInterval(this.poll);
      document.removeEventListener('change', this.onDocChange);
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
      if (this.onStickyClick) document.removeEventListener('click', this.onStickyClick, true);
      if (this.io) this.io.disconnect();
      if (this.sticky) {
        this.sticky.classList.remove('cts-sticky-hidden');
        this.restoreStickyLabel();
      }
    }

    /* ---------- theme sticky add-to-cart bar ---------- */

    setupSticky() {
      this.stickyMode = this.dataset.stickyMode || 'both';
      if (this.stickyMode === 'off') return;
      const sel = this.dataset.stickySelector || 'sticky-atc';
      this.sticky = this.scope.querySelector(sel) || document.querySelector(sel);
      if (!this.sticky) return;

      // Capture phase runs before the theme's own click handler on the sticky button.
      this.onStickyClick = (event) => {
        if (!this.toggle.checked || !this.sticky) return;
        const btn = event.target.closest(this.dataset.stickyButton || 'button');
        if (!btn || !this.sticky.contains(btn)) return;
        if (!this.mainVariant || !this.mainVariant.available) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.addBoth();
      };
      document.addEventListener('click', this.onStickyClick, true);

      this.bundleInView = false;
      if ('IntersectionObserver' in window) {
        // Ignore slivers hidden under a sticky header or behind the sticky bar itself.
        this.io = new IntersectionObserver(
          (entries) => {
            this.bundleInView = entries.some((e) => e.isIntersecting);
            this.updateSticky();
          },
          { rootMargin: '-100px 0px -100px 0px' }
        );
        this.io.observe(this);
      }
    }

    stickyLabelEl() {
      return this.sticky && this.sticky.querySelector(this.dataset.stickyLabel || '.sticky-atc__label__text');
    }

    restoreStickyLabel() {
      const label = this.stickyLabelEl();
      if (label && this.stickyOriginal != null && label.textContent === this.data.stickyText) {
        label.textContent = this.stickyOriginal;
      }
      this.stickyOriginal = null;
    }

    updateSticky() {
      if (!this.sticky) return;
      const on = this.toggle.checked;
      const hide = on && (this.stickyMode === 'hide' || this.bundleInView);
      this.sticky.classList.toggle('cts-sticky-hidden', hide);

      const label = this.stickyLabelEl();
      if (!label) return;
      const canAddBoth =
        on && this.stickyMode === 'both' && this.mainVariant && this.mainVariant.available;
      if (canAddBoth) {
        // The theme can rewrite this label on variant change, so re-apply each time.
        if (label.textContent !== this.data.stickyText) {
          this.stickyOriginal = label.textContent;
          label.textContent = this.data.stickyText;
        }
      } else {
        this.restoreStickyLabel();
      }
    }

    /* ---------- main product ---------- */

    getMainForm() {
      const forms = Array.from(
        this.scope.querySelectorAll('form[action*="/cart/add"]')
      ).filter((f) => !this.contains(f) && !f.closest('quick-add-modal, .quick-add-modal'));
      return (
        forms.find((f) => f.closest('product-form')) ||
        forms.find((f) => f.dataset.type === 'add-to-cart-form') ||
        forms[0] ||
        null
      );
    }

    readMain() {
      const form = this.getMainForm();
      const idInput = form && form.querySelector('[name="id"]');
      const id = idInput ? Number(idInput.value) : null;

      let qtyInput = null;
      if (form && form.id) {
        qtyInput = document.querySelector(`input[name="quantity"][form="${form.id}"]`);
      }
      if (!qtyInput && form) qtyInput = form.querySelector('input[name="quantity"]');
      if (!qtyInput) qtyInput = this.scope.querySelector('input[name="quantity"]');
      const qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

      const variant =
        this.data.main.find((v) => v.id === id) ||
        this.data.main.find((v) => v.available) ||
        this.data.main[0];
      return { variant, qty };
    }

    syncMain(force = false) {
      const { variant, qty } = this.readMain();
      if (!variant) return;
      const key = `${variant.id}:${qty}`;
      if (!force && key === this.lastMainKey) return;
      const variantChanged = !this.mainVariant || this.mainVariant.id !== variant.id;
      this.lastMainKey = key;
      this.mainVariant = variant;
      this.qty = qty;

      if (variantChanged && !this.userPickedSize) this.matchMainSize();
      this.render();
    }

    matchMainSize() {
      const idx = this.data.mainSizeIndex;
      if (idx < 0 || !this.mainVariant) return;
      const size = this.mainVariant.options[idx];
      const match = this.findUpsell(size);
      if (match && match.available) this.upsell = match;
    }

    /* ---------- add-on ---------- */

    findUpsell(size) {
      const candidates = this.upsellVariants.filter(
        (v) => v.options[this.sizeIndex] === size
      );
      if (!candidates.length) return null;
      const current = this.upsell;
      return (
        candidates.find((v) => current && sameExcept(v, current, this.sizeIndex) && v.available) ||
        candidates.find((v) => current && sameExcept(v, current, this.sizeIndex)) ||
        candidates.find((v) => v.available) ||
        candidates[0]
      );
    }

    onPillClick(pill) {
      if (pill.classList.contains('is-unavailable')) return;
      const match = this.findUpsell(pill.dataset.ctsSize);
      if (!match) return;
      this.userPickedSize = true;
      this.upsell = match;
      if (!this.toggle.checked) {
        this.toggle.checked = true;
        this.applyChecked();
      }
      this.render();
    }

    applyChecked() {
      const on = this.toggle.checked;
      this.classList.toggle('cts--checked', on);
      this.scope.classList.toggle('cts-checked', on);
      this.hideError();
      this.updateSticky();
    }

    /* ---------- render ---------- */

    money(cents) {
      return formatMoney(cents, this.data.moneyFormat);
    }

    render() {
      const up = this.upsell;
      const main = this.mainVariant;
      if (!up || !main) return;

      this.pills.forEach((pill) => {
        const match = this.findUpsell(pill.dataset.ctsSize);
        const available = Boolean(match && match.available);
        const selected = up.options[this.sizeIndex] === pill.dataset.ctsSize;
        pill.classList.toggle('is-unavailable', !available);
        pill.setAttribute('aria-pressed', String(selected));
        pill.setAttribute('aria-disabled', String(!available));
        pill.setAttribute(
          'aria-label',
          available ? pill.dataset.ctsSize : `${pill.dataset.ctsSize}, sold out`
        );
      });

      if (this.variantTitleEl) {
        this.variantTitleEl.textContent =
          up.title && up.title !== 'Default Title' ? ` · ${up.title}` : '';
      }
      if (this.priceEl) this.priceEl.textContent = this.money(up.price);
      if (this.compareEl) {
        const onSale = up.compare > up.price;
        this.compareEl.hidden = !onSale;
        this.compareEl.textContent = onSale ? this.money(up.compare) : '';
      }
      if (this.imageEl && up.image && this.imageEl.getAttribute('src') !== up.image) {
        this.imageEl.src = up.image;
      }

      const unitSavings = Math.max(0, (up.compare || 0) - up.price);
      if (this.badge) {
        this.badge.hidden = unitSavings <= 0;
        const percent = unitSavings > 0 ? `${Math.round((unitSavings * 100) / up.compare)}%` : '';
        const amount = this.money(unitSavings * this.qty).replace(/([.,])00(?!\d)/, '');
        this.badgeText.textContent = this.data.badgeText
          .replace('[percent]', percent)
          .replace('[amount]', amount);
      }

      const total = (main.price + up.price) * this.qty;

      if (!main.available) {
        this.setButton(`${this.data.buttonText} · Sold out`, true);
      } else if (!up.available) {
        this.setButton('Pick an available size', true);
      } else {
        this.setButton(`${this.data.buttonText} · ${this.money(total)}`, false);
      }
    }

    setButton(label, disabled) {
      this.updateSticky();
      if (this.loading) return;
      this.submitLabel.textContent = label;
      this.submit.disabled = disabled;
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    /* ---------- cart ---------- */

    async addBoth() {
      this.syncMain(true);
      const main = this.mainVariant;
      const up = this.upsell;
      if (!main || !up || !main.available || !up.available || this.loading) return;

      this.hideError();
      this.loading = true;
      this.submit.classList.add('is-loading');
      this.submit.setAttribute('aria-busy', 'true');
      this.submit.disabled = true;
      this.submitLabel.textContent = 'Adding...';

      const root = this.data.root && this.data.root.endsWith('/') ? this.data.root : `${this.data.root || ''}/`;
      const cartDrawer = document.querySelector('cart-drawer');
      const canRenderDrawer =
        cartDrawer &&
        typeof cartDrawer.renderContents === 'function' &&
        typeof cartDrawer.getSectionsToRender === 'function';

      const body = {
        items: [
          { id: main.id, quantity: this.qty },
          { id: up.id, quantity: this.qty },
        ],
      };
      if (canRenderDrawer) {
        body.sections = cartDrawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = window.location.pathname;
      }

      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await res.json();

        if (!res.ok || json.status) {
          throw new Error(json.description || json.message || 'Could not add these items. Try a different size.');
        }

        if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'complete-the-set',
            productVariantId: main.id,
            cartData: json,
          });
        }

        if (canRenderDrawer && json.sections) {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(json);
        } else {
          window.location.href = `${root}cart`;
          return;
        }
      } catch (err) {
        this.showError(err.message);
      } finally {
        this.loading = false;
        this.submit.classList.remove('is-loading');
        this.submit.removeAttribute('aria-busy');
        this.render();
      }
    }
  }

  customElements.define('complete-the-set', CompleteTheSet);
})();
